import * as React from "react";
import type { ColumnDef, Row, Table } from "@tanstack/react-table";

import { Checkbox } from "@/components/ui/checkbox";
import {
  applyDataTableRowCheckboxSelection,
  getDataTableSelectionMeta,
} from "@/components/ui/data-table-row-selection";

export type DataTableSelectColumnOptions<TData> = {
  /**
   * When provided, header "select all" is driven by this flag (Trade Log / Reports style).
   * When omitted, header uses TanStack all/some/indeterminate page selection.
   */
  selectAllFromHeader?: boolean;
  onSelectAllFromHeaderChange?: (viaHeader: boolean) => void;
  headerAriaLabel?: string;
  getRowAriaLabel?: (row: Row<TData>) => string;
  /** Extra attributes on header + row checkboxes (styling hooks). */
  checkboxDataAttributes?: Record<string, string | boolean | undefined>;
  headerDataAttributes?: Record<string, string | boolean | undefined>;
  /** When false, row cannot be selected (e.g. non-importable picks). */
  getRowCanSelect?: (row: Row<TData>) => boolean;
};

function selectionHandlerForTable<TData>(table: Table<TData>) {
  return getDataTableSelectionMeta(table.options.meta);
}

function toggleRowSelection<TData>(
  table: Table<TData>,
  row: Row<TData>,
  selected: boolean,
  shiftKey: boolean
): void {
  const meta = selectionHandlerForTable(table);
  const pageIds = table.getRowModel().rows.map((r) => r.id);
  const previous = table.getState().rowSelection;
  const { next, nextAnchorRowId } = applyDataTableRowCheckboxSelection({
    previous,
    visibleRowIds: pageIds,
    anchorRowId: meta?.getAnchor() ?? null,
    targetRowId: row.id,
    selected: shiftKey ? true : selected,
    shiftKey,
  });
  meta?.setAnchor(nextAnchorRowId);
  if (shiftKey) meta?.markShiftHandled();
  table.setRowSelection(next);
}

/**
 * Shared select column with Shift+click range selection (when used inside `DataTable`).
 */
export function createDataTableSelectColumn<TData>(
  options: DataTableSelectColumnOptions<TData> = {}
): ColumnDef<TData> {
  const {
    selectAllFromHeader,
    onSelectAllFromHeaderChange,
    headerAriaLabel = "Select all",
    getRowAriaLabel,
    checkboxDataAttributes,
    headerDataAttributes,
    getRowCanSelect,
  } = options;

  const useHeaderFlag = typeof selectAllFromHeader === "boolean";

  return {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        data-slot="data-table-select-all"
        data-trade-log-table-checkbox=""
        {...(checkboxDataAttributes as object)}
        {...(headerDataAttributes as object)}
        data-select-all-via-header={
          useHeaderFlag && selectAllFromHeader ? "true" : undefined
        }
        checked={
          useHeaderFlag
            ? selectAllFromHeader === true
            : table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => {
          const selectAll = !!value;
          onSelectAllFromHeaderChange?.(selectAll);
          table.toggleAllPageRowsSelected(selectAll);
        }}
        aria-label={headerAriaLabel}
      />
    ),
    cell: ({ row, table }) => {
      const canSelect = getRowCanSelect ? getRowCanSelect(row) : true;
      const meta = selectionHandlerForTable(table);

      return (
        <div
          data-trade-log-row-checkbox=""
          data-no-row-click=""
          className="flex h-full items-center select-none"
          onClick={(e) => e.stopPropagation()}
          onPointerDownCapture={(e) => {
            if (e.button !== 0 || !canSelect) return;
            meta?.notePointerShift(e.shiftKey);
            if (!e.shiftKey) return;
            e.preventDefault();
            e.stopPropagation();
            onSelectAllFromHeaderChange?.(false);
            toggleRowSelection(table, row, true, true);
          }}
        >
          <Checkbox
            data-slot="data-table-select-row"
            data-trade-log-table-checkbox=""
            {...(checkboxDataAttributes as object)}
            checked={row.getIsSelected()}
            disabled={!canSelect}
            onCheckedChange={(value) => {
              if (meta?.consumeShiftHandled()) return;
              onSelectAllFromHeaderChange?.(false);
              toggleRowSelection(table, row, !!value, false);
            }}
            aria-label={
              getRowAriaLabel?.(row) ?? "Select row"
            }
          />
        </div>
      );
    },
    enableSorting: false,
    enableHiding: false,
  };
}
